import { signalStore, withState, withMethods, withComputed, patchState } from '@ngrx/signals';
import { computed, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { MarquesService } from './marques.service';
import { CorrespondanceMarque } from '../../core/models/correspondance.model';

// Champs téléphone FR-22
const PHONE_FIELDS = new Set(['telephone_contact_CSC', 'telephone_depose_index', 'urgences_techniques']);

// Champs obligatoires quand percepteur=false
const REQUIRED_FIELDS_NON_PERCEPTEUR = new Set([
  'libelle_marque', 'MRQ_ADR1', 'MRQ_ADR2', 'MRQ_ADR3',
  'PND', 'adresse_marque', 'info_footer', 'info_juridiques',
  'lieu_emission', 'telephone_contact_CSC', 'telephone_depose_index',
]);

function isValidPhone(value: string): boolean {
  return value.replace(/[\s.]/g, '').length >= 4;
}

interface MarquesState {
  // Story 4.1 — recherche et consultation
  selectedMarque: CorrespondanceMarque | null;
  selectedCodeMarque: string | null;
  searchLoading: boolean;
  searchError: string | null;
  // Story 4.2 — édition inline
  editingFields: Record<string, string>;
  fieldErrors: Record<string, string | undefined>;
  saveLoading: boolean;
  saveError: string | null;
  // Story 4.3 — création
  createDialogOpen: boolean;
  createLoading: boolean;
  createError: string | null;
  // Story 4.4 — suppression
  deleteLoading: boolean;
  deleteError: string | null;
  // Upload logo
  uploadLogoLoading: boolean;
  uploadLogoError: string | null;
  // Liste de toutes les marques (pour recherche/filtrage)
  allMarques: { code_marque: string; libelle_marque?: string }[];
  allMarquesLoaded: boolean;
  // Mode édition
  editMode: boolean;
}

export const MarquesStore = signalStore(
  { providedIn: 'root' },
  withState<MarquesState>({
    selectedMarque: null,
    selectedCodeMarque: null,
    searchLoading: false,
    searchError: null,
    editingFields: {},
    fieldErrors: {},
    saveLoading: false,
    saveError: null,
    createDialogOpen: false,
    createLoading: false,
    createError: null,
    deleteLoading: false,
    deleteError: null,
    uploadLogoLoading: false,
    uploadLogoError: null,
    allMarques: [],
    allMarquesLoaded: false,
    editMode: false,
  }),
  withComputed((store) => ({
    hasUnsavedChanges: computed(() => Object.keys(store.editingFields()).length > 0),
    hasFieldErrors: computed(() => Object.keys(store.fieldErrors()).length > 0),
  })),
  withMethods((store, service = inject(MarquesService)) => ({

    // ------------------------------------------------------------------ Story 4.1

    async searchByCodeMarque(codeMarque: string): Promise<void> {
      patchState(store, {
        searchLoading: true, searchError: null, selectedMarque: null,
        selectedCodeMarque: null,
        editingFields: {}, fieldErrors: {}, saveError: null, deleteError: null,
        editMode: false,
      });
      try {
        const item = await firstValueFrom(service.getByCodeMarque(codeMarque));
        patchState(store, {
          searchLoading: false,
          selectedMarque: item,
          selectedCodeMarque: item.code_marque ?? codeMarque,
        });
      } catch (err: unknown) {
        const httpErr = err as HttpErrorResponse;
        let message = 'Erreur de chargement';
        if (httpErr?.status === 404) message = 'Donnée marque introuvable';
        else if (httpErr?.status === 403) message = 'Accès refusé à cette donnée marque';
        else if (httpErr?.error?.message) message = httpErr.error.message;
        patchState(store, { searchLoading: false, searchError: message });
      }
    },

    clearSelection(): void {
      patchState(store, {
        selectedMarque: null, selectedCodeMarque: null, searchError: null,
        editingFields: {}, fieldErrors: {}, saveError: null, editMode: false,
      });
    },

    enterEditMode(): void {
      patchState(store, { editMode: true });
    },

    exitEditMode(): void {
      patchState(store, { editMode: false, editingFields: {}, fieldErrors: {} });
    },

    // ------------------------------------------------------------------ Story 4.2

    setFieldValue(fieldName: string, value: string): void {
      patchState(store, { editingFields: { ...store.editingFields(), [fieldName]: value } });
    },

    cancelFieldEdit(fieldName: string): void {
      const fields = { ...store.editingFields() };
      delete fields[fieldName];
      const errors = { ...store.fieldErrors() };
      delete errors[fieldName];
      patchState(store, { editingFields: fields, fieldErrors: errors });
    },

    cancelAllEdits(): void {
      patchState(store, { editingFields: {}, fieldErrors: {}, saveError: null });
    },

    validateField(fieldName: string, value: string): void {
      const errors: Record<string, string | undefined> = { ...store.fieldErrors() };
      const isPercepteur = store.selectedMarque()?.percepteur === true
        || store.selectedMarque()?.percepteur === 'true';

      // Validation téléphone (FR-22)
      if (PHONE_FIELDS.has(fieldName)) {
        if (value && !isValidPhone(value)) {
          errors[fieldName] = 'Numéro de téléphone invalide (min. 4 chiffres)';
        } else {
          delete errors[fieldName];
        }
      }

      // Validation champs obligatoires quand percepteur=false (FR-22)
      if (!isPercepteur && REQUIRED_FIELDS_NON_PERCEPTEUR.has(fieldName)) {
        if (!value.trim()) {
          errors[fieldName] = 'Champ obligatoire';
        } else if (errors[fieldName] === 'Champ obligatoire') {
          delete errors[fieldName];
        }
      }

      patchState(store, { fieldErrors: errors });
    },

    async saveMarque(codeMarque: string): Promise<void> {
      patchState(store, { saveLoading: true, saveError: null });
      try {
        const current = store.selectedMarque();
        // N'envoyer que les champs réellement modifiés
        const changes: Record<string, string> = {};
        for (const [key, value] of Object.entries(store.editingFields())) {
          const currentValue = String((current as Record<string, unknown>)?.[key] ?? '');
          if (value !== currentValue) {
            changes[key] = value;
          }
        }
        if (Object.keys(changes).length > 0) {
          await firstValueFrom(service.updateMarque(codeMarque, changes));
        }
        // Rafraîchir la fiche (pour récupérer les valeurs après substitution de défauts)
        const updated = await firstValueFrom(service.getByCodeMarque(codeMarque));
        patchState(store, {
          selectedMarque: updated,
          editingFields: {},
          fieldErrors: {},
          saveLoading: false,
        });
      } catch (err: unknown) {
        const httpErr = err as HttpErrorResponse;
        let message = 'Erreur de sauvegarde';
        if (httpErr?.error?.message) message = httpErr.error.message;
        else if (httpErr?.status === 502) message = 'Erreur de synchronisation Tessi — modification annulée';
        else if (httpErr?.status === 403) message = 'Action non autorisée pour ce profil';
        patchState(store, { saveLoading: false, saveError: message });
      }
    },

    // ------------------------------------------------------------------ Story 4.3

    openCreateDialog(): void {
      patchState(store, { createDialogOpen: true, createError: null });
    },

    closeCreateDialog(): void {
      patchState(store, { createDialogOpen: false, createError: null });
    },

    async createMarque(data: Record<string, unknown>): Promise<void> {
      patchState(store, { createLoading: true, createError: null });
      try {
        await firstValueFrom(service.createMarque(data));
        patchState(store, { createLoading: false, createDialogOpen: false });
        // Rafraîchir la fiche après création
        const codeMarque = String(data['code_marque'] ?? '');
        if (codeMarque) {
          try {
            const created = await firstValueFrom(service.getByCodeMarque(codeMarque));
            patchState(store, { selectedMarque: created, searchError: null });
          } catch {
            // Non bloquant — la fiche peut être chargée manuellement
          }
        }
      } catch (err: unknown) {
        const httpErr = err as HttpErrorResponse;
        // Extraire le body parsé — Angular peut retourner un objet ou une string brute
        const errBody: { message?: string } =
          typeof httpErr?.error === 'string'
            ? (() => { try { return JSON.parse(httpErr.error); } catch { return {}; } })()
            : (httpErr?.error ?? {});
        let message = 'Erreur de création';
        if (httpErr?.status === 409) {
          message = 'Ce code_marque existe déjà.';
        } else if (httpErr?.status === 403) {
          message = 'Accès refusé — Éditeur ou OS&A requis.';
        } else if (httpErr?.status === 502) {
          message = 'Erreur de synchronisation Tessi — création annulée.';
        } else if (httpErr?.status === 422) {
          message = errBody?.message ?? 'Données invalides — vérifiez les champs obligatoires.';
        } else if (errBody?.message) {
          message = errBody.message;
        }
        patchState(store, { createLoading: false, createError: message });
      }
    },

    // ------------------------------------------------------------------ Story 4.4

    async deleteMarque(codeMarque: string): Promise<void> {
      patchState(store, { deleteLoading: true, deleteError: null });
      try {
        await firstValueFrom(service.deleteMarque(codeMarque));
        patchState(store, {
          deleteLoading: false,
          selectedMarque: null,
          editingFields: {},
          fieldErrors: {},
          saveError: null,
        });
      } catch (err: unknown) {
        const httpErr = err as HttpErrorResponse;
        const errBody: { message?: string } =
          typeof httpErr?.error === 'string'
            ? (() => { try { return JSON.parse(httpErr.error); } catch { return {}; } })()
            : (httpErr?.error ?? {});
        let message = 'Erreur de suppression';
        if (httpErr?.status === 502) {
          message = errBody?.message ?? 'Erreur de synchronisation Tessi — suppression annulée.';
        } else if (httpErr?.status === 403) {
          message = 'Suppression refusée — Éditeur ou OS&A requis.';
        } else if (httpErr?.status === 404) {
          message = 'Donnée marque introuvable.';
        } else if (errBody?.message) {
          message = errBody.message;
        }
        patchState(store, { deleteLoading: false, deleteError: message });
      }
    },

    async loadAllMarques(): Promise<void> {
      if (store.allMarquesLoaded()) return;
      try {
        const response = await firstValueFrom(service.listAll());
        patchState(store, { allMarques: response.items, allMarquesLoaded: true });
      } catch {
        // Non bloquant
      }
    },

    async uploadLogo(codeMarque: string, file: File): Promise<void> {
      patchState(store, { uploadLogoLoading: true, uploadLogoError: null });
      try {
        const result = await firstValueFrom(service.uploadLogo(codeMarque, file));
        const updated = await firstValueFrom(service.getByCodeMarque(codeMarque));
        const finalMarque = !updated.logo_presigned_url && result.logo_presigned_url
          ? { ...updated, logo_presigned_url: result.logo_presigned_url }
          : updated;
        patchState(store, { uploadLogoLoading: false, selectedMarque: finalMarque });
      } catch (err: unknown) {
        const httpErr = err as HttpErrorResponse;
        let message = "Erreur d'upload";
        if (httpErr?.error?.message) message = httpErr.error.message;
        else if (httpErr?.status === 502) message = 'Erreur de synchronisation Tessi — upload annulé';
        else if (httpErr?.status === 403) message = 'Upload refusé.';
        else if (httpErr?.status === 404) message = 'Donnée marque introuvable.';
        else if (httpErr?.status === 422 && httpErr?.error?.message) message = httpErr.error.message;
        patchState(store, { uploadLogoLoading: false, uploadLogoError: message });
      }
    },
  }))
);

